import React, { cloneElement, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, PhoneCall } from 'lucide-react';
import { toast } from 'react-toastify';

import PublicLayout from '../layouts/PublicLayout';
import Turnstile from '../common/Turnstile';
import { useCreateReachOutLead } from '../../services/leads';
// Same fixed dark-grey brand theme as /referral and the event pages.
import {
  INPUT,
  LABEL,
  PAGE_BG,
  useBrandBodyBackground,
} from '../referral/brandTheme';
import VaGovAccountQuestion from '../referral/VaGovAccountQuestion';
import { formatUsPhone, isUsPhone, usPhoneDigits } from '../../lib/usPhone';
import MilitaryQuestions, {
  EMPTY_MILITARY,
  militaryAnswers,
} from './MilitaryQuestions';

// Per-toast theming for this page only: the default white toasts look broken
// against the dark brand theme, and the app-default 5s linger is too long
// for a short form (2.5s tested right - 1.5s was too quick). Same recipe as
// /register - kept local since the pages theme independently.
const TOAST = { theme: 'dark', className: '!rounded-xl', autoClose: 2500 };

const Field = ({ id, label, required, children }) => (
  <div>
    <label htmlFor={id} className={LABEL}>
      {label}
      {required ? <span className="text-rose-400"> *</span> : null}
    </label>
    {cloneElement(children, { id })}
  </div>
);

// Public self-lead intake (/reach-out): the marketing site's "have us reach
// out" form. Replaces the old Contact Us dummy form's lead traffic - a plain
// Website-channel lead that rides the standard board arrival (rotation +
// 10-day cadence), no partner or fake event involved. General contact stays
// an email form on the marketing site; this page is only for people who want
// a call about their rating.
const ReachOut = () => {
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    vaGovAccount: '', // 'yes' | 'no' | 'unsure'
  });
  const [military, setMilitary] = useState(EMPTY_MILITARY);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  useBrandBodyBackground();

  const submitLead = useCreateReachOutLead(() => setDone(true), {
    onError: (err) => {
      // Turnstile tokens are single-use - the server consumed this one even on
      // a 4xx, so reset the widget or every retry re-sends the spent token.
      turnstileRef.current?.reset();
      toast.error(
        err?.response?.data?.msg || 'Something went wrong. Please try again.',
        TOAST,
      );
    },
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhone = (e) =>
    setForm((f) => ({ ...f, phone: formatUsPhone(e.target.value) }));
  const militaryState = militaryAnswers(military);
  const fieldsComplete =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim() &&
    isUsPhone(form.phone) &&
    militaryState.complete &&
    form.vaGovAccount;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!fieldsComplete || !turnstileToken) {
      // When the fields are all fine and only the Turnstile token is missing
      // (unsolved/expired), say so instead of blaming the fields.
      toast.error(
        fieldsComplete && !turnstileToken
          ? 'Please complete the security check below.'
          : 'Please complete all of the fields.',
        TOAST,
      );
      return;
    }
    submitLead.mutate({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: usPhoneDigits(form.phone),
      isVeteran: militaryState.isVeteran,
      rank: militaryState.rank,
      vaGovAccount: form.vaGovAccount,
      turnstileToken,
    });
  };

  return (
    <PublicLayout
      background={PAGE_BG}
      footerClassName="text-neutral-300"
      footerVariant="dark"
    >
      <div className="mx-auto max-w-md py-6">
        <div className="rounded-2xl bg-[#4A4A4E] p-6 shadow-lg sm:p-8">
          {done ? (
            <div className="py-4 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/10 text-emerald-400">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-white">You're all set!</h2>
              <p className="mt-2 text-sm text-neutral-300">
                Thanks for reaching out. Our team will be in touch within 24-48
                hours.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-5 text-center">
                <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10">
                  <PhoneCall className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">
                  Have Us Reach Out
                </h2>
                <p className="text-center text-sm text-neutral-300">
                  Leave your info and our team will reach out within 24-48 hours
                  to talk about your VA disability rating.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field id="reach-firstName" label="First name" required>
                    <input
                      className={INPUT}
                      autoComplete="given-name"
                      value={form.firstName}
                      onChange={set('firstName')}
                    />
                  </Field>
                  <Field id="reach-lastName" label="Last name" required>
                    <input
                      className={INPUT}
                      autoComplete="family-name"
                      value={form.lastName}
                      onChange={set('lastName')}
                    />
                  </Field>
                  <Field id="reach-email" label="Email" required>
                    <input
                      className={INPUT}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set('email')}
                    />
                  </Field>
                  <Field id="reach-phone" label="Phone" required>
                    <input
                      className={INPUT}
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={setPhone}
                      placeholder="(904) 555-1212"
                    />
                  </Field>
                </div>

                <MilitaryQuestions value={military} onChange={setMilitary} />

                <VaGovAccountQuestion
                  value={form.vaGovAccount}
                  onChange={(v) => setForm((f) => ({ ...f, vaGovAccount: v }))}
                />

                <Turnstile ref={turnstileRef} onToken={setTurnstileToken} />

                <button
                  type="submit"
                  disabled={submitLead.isLoading}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-white/10 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-200 hover:!text-black disabled:pointer-events-none disabled:opacity-50"
                >
                  {submitLead.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  Have us reach out
                </button>

                <p className="text-center text-xs text-neutral-400">
                  Ready to dive in now?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="font-medium text-neutral-300 underline hover:text-white"
                  >
                    Create your account
                  </button>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

export default ReachOut;
